/**
 * Connections admin handler
 * Manages Nango OAuth connections for participants
 */

import { useState, useCallback, useEffect } from "react";
import type { UseAdminAppReturn } from "../useAdminApp";

export interface Connection extends Record<string, unknown> {
  connectionId: string;
  integrationId: string;
  participantId: string;
  provider: string;
  createdAt: string;
  endUserId?: string;
  metadata?: Record<string, unknown>;
}

export interface ConnectionCredentials {
  participantId: string;
  integrationId: string;
  connectionId: string;
  credentials: {
    access_token?: string;
    refresh_token?: string;
    expires_at?: string;
    [key: string]: unknown;
  };
  metadata?: Record<string, unknown>;
}

export interface ConnectionsAdminHandler {
  connections: Connection[];
  loading: boolean;
  error: string | null;
  loadConnections: () => Promise<void>;
  loadParticipantConnections: (participantId: string) => Promise<Connection[]>;
  createAuthUrl: (participantId: string, integrationId: string) => Promise<string>;
  deleteConnection: (participantId: string, integrationId: string) => Promise<void>;
  getCredentials: (participantId: string, integrationId: string) => Promise<ConnectionCredentials>;
  refresh: () => Promise<void>;
}

export function useConnectionsAdmin(adminApp: UseAdminAppReturn): ConnectionsAdminHandler {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all connection IDs from Nango
  const loadConnections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/integrator/connection-ids`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load connections');
      const data = await response.json();
      
      // Normalize API response to match Connection interface
      const normalized = (data.connections || []).map((conn: any) => ({
        connectionId: conn.connectionId,
        integrationId: conn.providerConfigKey,
        participantId: conn.endUserId || 'unknown',
        provider: (conn.providerConfigKey || '').split('_')[0] || 'unknown',
        createdAt: conn.createdAt,
        endUserId: conn.endUserId,
        metadata: conn.metadata,
      }));
      
      setConnections(normalized);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      console.error('Failed to load connections:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Load connections for specific participant
  const loadParticipantConnections = useCallback(async (participantId: string): Promise<Connection[]> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/integrator/connections/${participantId}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to load participant connections');
      const data = await response.json();
      return data.connections || [];
    } catch (err) {
      console.error('Failed to load participant connections:', err);
      return [];
    }
  }, [adminApp.httpUrl]);

  // Generate OAuth URL for participant
  const createAuthUrl = useCallback(async (participantId: string, integrationId: string): Promise<string> => {
    const response = await fetch(
      `${adminApp.httpUrl}/api/integrator/auth-url?participantId=${participantId}&integrationId=${integrationId}`,
      { credentials: 'include' }
    );
    if (!response.ok) throw new Error('Failed to generate auth URL');
    const data = await response.json();
    return data.authUrl;
  }, [adminApp.httpUrl]);

  // Delete connection
  const deleteConnection = useCallback(async (participantId: string, integrationId: string) => {
    await fetch(
      `${adminApp.httpUrl}/api/integrator/connection?participantId=${participantId}&integrationId=${integrationId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );
    await loadConnections(); // Refresh list
  }, [adminApp.httpUrl, loadConnections]);

  // Get raw credentials
  const getCredentials = useCallback(async (
    participantId: string,
    integrationId: string
  ): Promise<ConnectionCredentials> => {
    const response = await fetch(
      `${adminApp.httpUrl}/api/integrator/credentials/${participantId}/${integrationId}`,
      { credentials: 'include' }
    );
    if (!response.ok) throw new Error('Failed to get credentials');
    return await response.json();
  }, [adminApp.httpUrl]);

  // Auto-load on mount and workspace change
  useEffect(() => {
    loadConnections();
  }, [loadConnections, adminApp.workspaceId]);

  return {
    connections,
    loading,
    error,
    loadConnections,
    loadParticipantConnections,
    createAuthUrl,
    deleteConnection,
    getCredentials,
    refresh: loadConnections,
  };
}

